import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MA_CATS, type CategoryId } from "@myams/shared";
import {
  MaBottomNav, MaCircleButton, MaIcon, MaSectionHeader, MaTopBar, type IconName,
} from "../components/ui";
import { Screen } from "../components/layout/Screen";
import { useImportedEvents, useVenues } from "../lib/queries";

const CAT_ROWS: { id: CategoryId; label: string }[] = [
  { id: "music",   label: "Music & Nightlife" },
  { id: "food",    label: "Food & Drink" },
  { id: "art",     label: "Art & Exhibits" },
  { id: "market",  label: "Markets" },
  { id: "cinema",  label: "Cinema & Film" },
  { id: "outdoor", label: "Outdoors" },
  { id: "cafe",    label: "Cafés & Coffee" },
  { id: "queer",   label: "Queer Nightlife" },
  { id: "sports",  label: "Sports & Events" },
  { id: "hidden",  label: "Hidden / Local" },
];

export function Category() {
  const navigate = useNavigate();
  const { data: venues = [] } = useVenues();
  const { data: events = [] } = useImportedEvents({ upcoming: true, limit: 200 });

  // Counts: actual venues + actual imported events per category
  const counts = useMemo(() => {
    const m: Partial<Record<CategoryId, { venues: number; events: number; live: number }>> = {};
    for (const v of venues) {
      const c = (v.cat as CategoryId);
      const entry = m[c] ?? { venues: 0, events: 0, live: 0 };
      entry.venues++; if (v.live) entry.live++;
      m[c] = entry;
    }
    for (const e of events) {
      if (!e.category) continue;
      const entry = m[e.category as CategoryId] ?? { venues: 0, events: 0, live: 0 };
      entry.events++;
      m[e.category as CategoryId] = entry;
    }
    return m;
  }, [venues, events]);

  // Neighborhood counts from real data
  const neighCounts = useMemo(() => {
    const m: Record<string, { venues: number; events: number }> = {};
    for (const v of venues) {
      const k = v.neigh; if (!k) continue;
      m[k] = m[k] ?? { venues: 0, events: 0 }; m[k].venues++;
    }
    for (const e of events) {
      const k = e.neighborhood; if (!k) continue;
      m[k] = m[k] ?? { venues: 0, events: 0 }; m[k].events++;
    }
    return Object.entries(m)
      .map(([name, c]) => ({ name, n: c.venues + c.events, venues: c.venues, events: c.events }))
      .sort((a, b) => b.n - a.n).slice(0, 8);
  }, [venues, events]);

  const totalSpots = venues.length + events.length;

  return (
    <Screen footer={<MaBottomNav active="discover" />}>
      <MaTopBar
        title="Browse"
        subtitle={`${totalSpots} spots · live from sources`}
        leading={<MaCircleButton icon="back" onClick={() => navigate("/discover")} />}
        trailing={<MaCircleButton icon="search" onClick={() => navigate("/search")} />}
      />

      <div style={{ padding: "0 16px 16px" }}>
        <MaSectionHeader label="Browse by interest" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {CAT_ROWS.map((c, i) => {
            const cat = MA_CATS[c.id];
            const n = counts[c.id];
            const total = (n?.venues ?? 0) + (n?.events ?? 0);
            const tall = i === 0 || i === 5;
            return (
              <button key={c.id} onClick={() => navigate(`/discover?cat=${c.id}`)} style={{
                position: "relative",
                background: cat.color, color: "var(--ma-paper)",
                border: "var(--ma-border) solid var(--ma-ink)",
                borderRadius: "var(--ma-card-radius)",
                padding: "14px 14px 12px",
                minHeight: tall ? 130 : 110,
                overflow: "hidden", textAlign: "left",
                display: "flex", flexDirection: "column", justifyContent: "space-between",
                cursor: "pointer",
                opacity: total === 0 ? 0.55 : 1,
              }}>
                <div style={{
                  position: "absolute", top: 0, right: 0, width: 60, height: 60,
                  background: "repeating-linear-gradient(135deg, transparent 0 4px, rgba(0,0,0,0.18) 4px 5px)",
                  pointerEvents: "none",
                }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <MaIcon name={c.id as IconName} size={22} stroke={1.6} color="var(--ma-paper)" />
                  <span className="ma-mono" style={{ fontSize: 9, letterSpacing: "0.1em", opacity: 0.75 }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div>
                  <div className="ma-display" style={{ fontSize: tall ? 22 : 18, lineHeight: 1, color: "var(--ma-paper)", marginBottom: 4 }}>
                    {c.label}
                  </div>
                  <div className="ma-mono" style={{ fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.8 }}>
                    {total === 0 ? "none yet" : `${total} ${total === 1 ? "spot" : "spots"}${n?.live ? ` · ${n.live} live` : ""}`}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {neighCounts.length > 0 && (
        <div style={{ padding: "8px 16px 16px" }}>
          <MaSectionHeader label="By neighbourhood" action="Map view" onAction={() => navigate("/discover")} />
          <div style={{
            background: "var(--ma-cream)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            overflow: "hidden",
          }}>
            {neighCounts.map((n, i, arr) => (
              <button key={n.name} onClick={() => navigate(`/discover?neigh=${encodeURIComponent(n.name)}`)} style={{
                display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
                borderBottom: i === arr.length - 1 ? "none" : "1px dashed var(--ma-line)",
                background: "transparent", border: "none", width: "100%", textAlign: "left", cursor: "pointer",
              }}>
                <div style={{
                  width: 32, height: 32, background: "var(--ma-ink)", color: "var(--ma-paper)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "var(--ma-mono)", fontSize: 11, fontWeight: 600,
                }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="ma-display" style={{ fontSize: 18, color: "var(--ma-ink)" }}>{n.name}</div>
                  <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ma-ink-soft)", marginTop: 1 }}>
                    {n.venues} venues · {n.events} events
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="ma-display" style={{ fontSize: 22, color: "var(--ma-ink)", lineHeight: 1 }}>{n.n}</div>
                  <div className="ma-mono" style={{ fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ma-ink-soft)" }}>
                    spots
                  </div>
                </div>
                <MaIcon name="arrow" size={16} stroke={1.6} color="var(--ma-ink-soft)" />
              </button>
            ))}
          </div>
        </div>
      )}

    </Screen>
  );
}
